import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Content from "../../layout/content/Content";
import {
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Badge,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Head from "../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Table,
  Collapse as AntCollapse,
  TableColumnsType,
  Descriptions,
  Modal,
  Timeline,
  Popover,
  Empty,
  Flex,
  Spin,
  Button as AntButton,
  Tag,
} from "antd";
import {
  PaperClipOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import WorkBoqAside from "./workBoqAside";
import { formatDecimal } from "../../utils/Utils";
import WorkBoqPdfGenerator from "./WorkBoqPdfGenerator";
import { useSelector } from "react-redux";

import workBoqApis from "../../api/WorkOrderManagement/WorkBOQ";
import { message } from "antd";

const { Panel } = AntCollapse;

interface WorkBoqViewData {
  id: string;
  boqTitle: string;
  boqDescription: string;
  siteName: string;
  projectName?: string;
  contractorName?: string;
  workLevel: string;
  materialBoqName: string;
  budgetName?: string;
  completionAlertsFrom?: string;
  status: "Draft" | "Pending Approval" | "Approved" | "Rejected";
  createdAt: string;
  createdBy: string;
  submittedAt?: string;
  submittedBy?: string;
  approvals?: any[];
  rejectedReason?: string;
  workOrderLines: any[];
  instructions?: string;
  attachments?: any;
  approvalStatus?: string;
}

const WorkBoqView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [workBoqData, setWorkBoqData] = useState<WorkBoqViewData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sm, setSm] = useState(false);
  const [tab, setTab] = useState("1");
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [disableApprove, setDisableApprove] = useState(false);
  const [disableReject, setDisableReject] = useState(false);
  const [shareOptionsToggle, setShareOptionsToggle] = useState(false);
  const [instructionsVisible, setInstructionsVisible] = useState(false);

  // Mock current user permissions
  const [allowedActions] = useState({
    view: 1,
    edit: 1,
    approve: 1,
    reject: 1,
  });

  const [currentUserId] = useState(1); // Mock current user ID
  const [approverIds] = useState([1, 2, 3]); // Mock approver IDs

  useEffect(() => {
    fetchWorkBoqDetails();
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, [id]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const fetchWorkBoqDetails = async () => {
    try {
      if (!id) {
        message.error("Work BOQ ID is required");
        setIsLoading(false);
        return;
      }

      // Call the actual API
      const response: any = await workBoqApis.getWorkBoqById(id);

      if (
        response &&
        response.status &&
        response.data &&
        response.data.workBoq
      ) {
        const apiData = response.data.workBoq;

        // Map work order lines with proper structure
        const mappedWorkOrderLines =
          apiData.workBoqLines?.map((line: any) => ({
            id: line.workBoqLineId?.toString() || "",
            workCategory: line.workCategoryId?.toString() || "",
            workCategoryName: line.workCategoryName || "",
            workSubCategory: line.workSubCategoryId?.toString() || "",
            taskTitle: line.taskTitle || "",
            taskDescription: line.taskDescription || "",
            quantity: line.quantity || 0,
            uom: line.uomId?.toString() || "",
            uomName: line.uomName || "",
            towerVilla: line.towerVilla || "",
            floorName: line.floorName || "",
            flatName: line.flatName || "",
            budgetLineId: line.budgetLineId || null,
            budgetLineName: line.budgetLineName || "",
            budgetAllocation: line.budgetAllocation || 0,
            allocatedMaterials:
              line.workBoqLineMaterials?.map((material: any) => ({
                materialId: material.materialId?.toString() || "",
                materialName: material.materialName || "Unknown Material",
                quantity: material.allocatedQty || 0,
                categoryId: material.categoryId?.toString() || "",
                category: material.categoryName || "",
                uom: line.uomName || "", // Use UOM from the parent line
                uomName: line.uomName || "",
              })) || [],
            uploadedDocs: line.dms || [],
          })) || [];

        // Map API response to WorkBoqViewData interface
        const workBoqData: WorkBoqViewData = {
          id: id || "",
          boqTitle: apiData.boqTitle || "",
          boqDescription: apiData.boqDescription || "",
          siteName: apiData.siteName || "",
          projectName: apiData.projectTitle || "N/A",
          contractorName: apiData.contractorName || undefined,
          workLevel: apiData.segmentLevel || "",
          materialBoqName: apiData.materialBoqName || "N/A", // Not provided in response
          budgetName: apiData.budgetName || undefined,
          completionAlertsFrom:
            apiData.completionAlertsFrom?.toString() || undefined,
          status: apiData.status || "Draft",
          createdAt: apiData.createdAt || new Date().toISOString(),
          createdBy: apiData.createdByName || "System User",
          submittedAt: apiData.updatedAt || undefined,
          submittedBy: apiData.createdByName || undefined,
          approvals: apiData.workBoqApprovals || [],
          rejectedReason: apiData.rejectedReason || undefined,
          workOrderLines: mappedWorkOrderLines,
          instructions: apiData.instructions || undefined,
          attachments: apiData.attachments || undefined,
          approvalStatus: apiData.approvalStatus || undefined,
        };

        setWorkBoqData(workBoqData);
        message.success("Work BOQ details loaded successfully");
      } else {
        throw new Error(
          response?.message || "Failed to fetch Work BOQ details"
        );
      }
    } catch (error: any) {
      console.error("Error fetching Work BOQ details:", error);
      message.error("Failed to fetch Work BOQ details");
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.message ||
          "Failed to fetch Work BOQ details. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Work order lines table columns
  const [columns] = useState<TableColumnsType<any>>([
    // {
    //   ...Table.EXPAND_COLUMN,
    //   fixed: "left",
    // },
    {
      title: "#",
      key: "#",
      width: 50,
      fixed: "left",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Work Category",
      dataIndex: "workCategory",
      key: "workCategory",
      width: 150,
      fixed: "left",
      render: (text, record) => record.workCategoryName || text || "-",
    },
    // {
    //   title: "Sub Category",
    //   dataIndex: "workSubCategory",
    //   key: "workSubCategory",
    //   width: 150,
    //   render: (text) => text || "-",
    // },
    {
      title: "Task Title",
      dataIndex: "taskTitle",
      key: "taskTitle",
      width: 200,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      render: (text, record) =>
        `${formatDecimal(text)} ${record?.uomName || record?.uom || ""}`,
    },
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: 80,
      render: (text, record) => record?.uomName || text || "-",
    },
    {
      title: "Location",
      key: "location",
      width: 150,
      hidden: workBoqData?.workLevel === "Project",
      render: (text, record) => {
        const location = [];
        if (record.towerVilla) location.push(record.towerVilla);
        if (record.floorName) location.push(record.floorName);
        if (record.flatName) location.push(record.flatName);
        return location.join(" / ") || "-";
      },
    },
    {
      title: "Materials",
      key: "materials",
      width: 120,
      render: (text, record) => (
        <Badge color="outline-info">
          {record.allocatedMaterials?.length || 0} items
        </Badge>
      ),
    },
    {
      title: "Budget",
      key: "budget",
      width: 120,
      render: (text, record) => (
        <div>
          {record.budgetLineName ? (
            <div style={{ textAlign: "center" }}>
              <Tag color="green" style={{ fontSize: "10px", margin: 0 }}>
                {record.budgetLineName}
              </Tag>
              <div style={{ fontSize: "11px", color: "#666", marginTop: "2px" }}>
                ₹{formatDecimal(record.budgetAllocation || 0)}
              </div>
            </div>
          ) : (
            <span style={{ color: "#999", fontSize: "11px" }}>No budget</span>
          )}
        </div>
      ),
    },
    {
      title: "Documents",
      key: "documents",
      width: 100,
      render: (text, record) =>
        record.uploadedDocs?.length > 0 ? (
          <CheckCircleOutlined style={{ color: "#28a745" }} />
        ) : (
          <CloseCircleOutlined style={{ color: "#dc3545" }} />
        ),
    },
  ]);

  const handleApprove = async () => {
    try {
      setDisableApprove(true);

      if (!id) {
        message.error("Work BOQ ID is required");
        return;
      }

      // Prepare payload for approval API
      const approvalPayload = {
        workBoqId: parseInt(id),
        action: "Approved",
      };

      console.log("Approving Work BOQ with payload:", approvalPayload);

      // Call the actual approval API
      const response: any = await workBoqApis.approveWorkBoq(approvalPayload);

      if (response && response.status) {
        // Update local state
        setWorkBoqData((prev) =>
          prev ? { ...prev, status: "Approved" as const } : null
        );

        message.success("Work BOQ approved successfully!");
        Swal.fire(
          "Approved!",
          response.message || "Work BOQ has been approved successfully",
          "success"
        ).then(() => {
          navigate("/work-boq-list");
        });
      } else {
        throw new Error(response?.message || "Failed to approve Work BOQ");
      }
    } catch (error: any) {
      console.error("Error approving Work BOQ:", error);
      message.error("Failed to approve Work BOQ");
      Swal.fire(
        "Error!",
        error.message || "Failed to approve Work BOQ. Please try again.",
        "error"
      );
    } finally {
      setDisableApprove(false);
    }
  };

  const handleReject = async (rejectedReason: string) => {
    try {
      setDisableReject(true);

      if (!id) {
        message.error("Work BOQ ID is required");
        return;
      }

      if (!rejectedReason?.trim()) {
        message.error("Rejection reason is required");
        return;
      }

      // Prepare payload for rejection API
      const rejectionPayload = {
        workBoqId: parseInt(id),
        action: "Rejected",
        comments: rejectedReason.trim(),
      };

      console.log("Rejecting Work BOQ with payload:", rejectionPayload);

      // Call the actual rejection API
      const response: any = await workBoqApis.rejectWorkBoq(rejectionPayload);

      if (response && response.status) {
        // Update local state
        setWorkBoqData((prev) =>
          prev
            ? {
                ...prev,
                status: "Rejected" as const,
                rejectedReason,
              }
            : null
        );

        message.success("Work BOQ rejected successfully!");
        Swal.fire(
          "Rejected!",
          response.message || "Work BOQ has been rejected successfully",
          "success"
        ).then(() => {
          navigate("/work-boq-list");
        });
      } else {
        throw new Error(response?.message || "Failed to reject Work BOQ");
      }
    } catch (error: any) {
      console.error("Error rejecting Work BOQ:", error);
      message.error("Failed to reject Work BOQ");
      Swal.fire(
        "Error!",
        error.message || "Failed to reject Work BOQ. Please try again.",
        "error"
      );
    } finally {
      setDisableReject(false);
    }
  };

  const showRejectModal = () => {
    Swal.fire({
      title: "Reject Work BOQ",
      html: `
        <textarea 
          id="rejection-reason" 
          placeholder="Enter rejection reason (required)"
          style="width: 100%; height: 100px; margin-top: 10px; padding: 8px; border: 1px solid #ddd; border-radius: 4px;"
        ></textarea>
      `,
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Reject",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        const reason = (
          document.getElementById("rejection-reason") as HTMLTextAreaElement
        )?.value;
        if (!reason.trim()) {
          Swal.showValidationMessage("Rejection reason is required");
          return false;
        }
        return reason;
      },
    }).then((result: any) => {
      if (result.isConfirmed) {
        setDisableReject(true);
        handleReject(result.value);
      }
    });
  };

  const ApprovalTimelineContent = () => {
    const approvalsData = workBoqData?.approvals;

    if (!approvalsData || approvalsData?.length === 0) {
      return <Badge color="outline-danger">No</Badge>;
    }

    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.map((approval: any, index: number) => {
              const isApproved = approval.status === "Approved";
              const isPending = approval.status === "Pending";
              return (
                <Timeline.Item
                  key={index}
                  dot={
                    isApproved ? (
                      <CheckCircleOutlined style={{ color: "green" }} />
                    ) : isPending ? (
                      <CloseCircleOutlined style={{ color: "orange" }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: "red" }} />
                    )
                  }
                >
                  {approval?.userName}
                  <div className="designation">{approval?.roleName}</div>
                  <div className="designation">{approval?.approvalStage}</div>
                  {approval?.approvalDate && (
                    <div className="designation">
                      {dayjs(approval?.approvalDate).format("DD-MMM-YYYY")}
                    </div>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2263b3" }}>Approval Hierarchy</span>}
      >
        <span
          style={{
            color: "#2263b3",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          <Badge color="outline-success">Yes</Badge>
        </span>
      </Popover>
    );
  };

  const getStatusMapper = (status: string) => {
    const statusMap: { [key: string]: { title: string; color: string } } = {
      Draft: { title: "Draft", color: "secondary" },
      "Pending Approval": { title: "Pending Approval", color: "warning" },
      Approved: { title: "Approved", color: "success" },
      Rejected: { title: "Rejected", color: "danger" },
    };
    return statusMap[status] || { title: status, color: "secondary" };
  };

  if (isLoading) {
    return (
      <>
        <Head title="Work BOQ Details" />
        <Content>
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        </Content>
      </>
    );
  }

  if (!workBoqData) {
    return (
      <>
        <Head title="Work BOQ Not Found" />
        <Content>
          <div className="text-center py-5">
            <Empty description="Work BOQ not found" />
          </div>
        </Content>
      </>
    );
  }

  const statusInfo = getStatusMapper(workBoqData.status);

  return (
    <>
      <Head title={`Work BOQ - ${workBoqData.boqTitle}`} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">
                      <span>
                        {workBoqData.boqTitle} | {statusInfo.title}
                      </span>
                    </BlockTitle>
                  </BlockHeadContent>
                  <div className="d-flex">
                    {["Pending"].includes(workBoqData?.approvalStatus ?? "") &&
                      allowedActions?.approve === 1 &&
                      approverIds.includes(currentUserId) && (
                        <div className="actions clearfix">
                          <ul
                            className="m-auto d-flex align-items-center justify-content-center p-0"
                            style={{
                              flexDirection: "row-reverse",
                            }}
                          >
                            <li className="pt-0 pb-0">
                              {disableApprove ? (
                                <Button color="success" type="button">
                                  <Spinner size="sm" color="light" />
                                </Button>
                              ) : (
                                <AntButton
                                  type="primary"
                                  icon={<CheckCircleOutlined />}
                                  onClick={() => {
                                    Swal.fire({
                                      title: "Are you sure?",
                                      text: "Do you want to approve this Work BOQ?",
                                      icon: "warning",
                                      showCancelButton: true,
                                      confirmButtonText: "Yes, Approve it!",
                                      cancelButtonText: "No, keep it",
                                    }).then((result) => {
                                      if (result.isConfirmed) {
                                        handleApprove();
                                      }
                                    });
                                  }}
                                >
                                  Approve
                                </AntButton>
                              )}
                            </li>
                            <li className="pt-0 pb-0">
                              {disableReject ? (
                                <Button color="danger" type="button">
                                  <Spinner size="sm" color="light" />
                                </Button>
                              ) : (
                                <AntButton
                                  danger
                                  icon={<CloseCircleOutlined />}
                                  onClick={showRejectModal}
                                >
                                  Reject
                                </AntButton>
                              )}
                            </li>
                          </ul>
                        </div>
                      )}

                    <div className="actions clearfix">
                      <ul
                        className="m-auto d-flex align-items-center justify-content-center p-0"
                        style={{
                          flexDirection: "row-reverse",
                        }}
                      >
                        <li className="pt-0 pb-0">
                          <Dropdown
                            isOpen={shareOptionsToggle}
                            toggle={() => {
                              setShareOptionsToggle(!shareOptionsToggle);
                            }}
                          >
                            {/* <DropdownToggle className="btn btn-white btn-outline-light">
                              <Icon name="share"></Icon>
                              <span>Share</span>
                            </DropdownToggle> */}
                            <DropdownMenu>
                              {/* <DropdownItem onClick={() => {}}>
                                <Icon name="mail"></Icon>
                                <span>Send Mail</span>
                              </DropdownItem> */}
                              {/* <DropdownItem onClick={() => {}}>
                                <Icon name="download"></Icon>
                                <span>Export PDF</span>
                              </DropdownItem> */}
                            </DropdownMenu>
                          </Dropdown>
                        </li>
                        {/* <li className="pt-0 pb-0">
                          <WorkBoqPdfGenerator
                            workBoqData={workBoqData}
                            orgLogo={currentUser?.orgLogo}
                            orgName={currentUser?.orgName}
                            getPdfUrl={() => {}}
                          />
                        </li> */}

                        <li className="pt-0 pb-0">
                          <Button
                            className="toggle"
                            color="primary"
                            onClick={() => {
                              navigate("/work-boq-list");
                            }}
                          >
                            <Icon name="arrow-left" />
                            <span>Back</span>
                          </Button>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <BlockHeadContent className="align-self-start d-lg-none">
                    <Button
                      className={`toggle btn btn-icon btn-trigger mt-n1 ${
                        sm ? "active" : ""
                      }`}
                      onClick={() => setSm(!sm)}
                    >
                      <Icon name="menu-alt-r" />
                    </Button>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>

              <Alert
                color={"danger"}
                isOpen={workBoqData.status === "Rejected"}
              >
                <strong>Rejected: </strong>
                {workBoqData.rejectedReason || "No reason provided"}
              </Alert>

              <div className="content clearfix">
                <AntCollapse defaultActiveKey={["1", "2", "3"]}>
                  <Panel
                    header={
                      <div className="d-flex align-items-center justify-content-between">
                        <>General Information</>
                      </div>
                    }
                    key="1"
                  >
                    <Col className="justify-start w-100 flex-wrap mt-1 p-1">
                      <div className="w-30 border-end">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">BOQ Title</div>
                          <div className="data-value w-70">
                            {workBoqData.boqTitle || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Site</div>
                          <div className="data-value w-70">
                            {workBoqData.siteName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Project</div>
                          <div className="data-value w-70">
                            {workBoqData.projectName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Approvals</div>
                          <div className="data-value w-70">
                            <ApprovalTimelineContent />
                          </div>
                        </div>
                      </div>
                      <div className="w-30 border-end paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Contractor</div>
                          <div className="data-value w-70">
                            {workBoqData.contractorName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Material BOQ</div>
                          <div className="data-value w-70">
                            {workBoqData.materialBoqName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Budget</div>
                          <div className="data-value w-70">
                            {workBoqData.budgetName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Work Level</div>
                          <div className="data-value w-70">
                            <Badge color="outline-primary">
                              {workBoqData.workLevel || "--"}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Status</div>
                          <div className="data-value w-70">
                            <Badge color={`outline-${statusInfo.color}`}>
                              {statusInfo.title}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="w-30 paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Description</div>
                          <div className="data-value w-70">
                            <Popover
                              content={
                                <div>
                                  <p>{workBoqData.boqDescription}</p>
                                </div>
                              }
                              title={"BOQ Description"}
                            >
                              {workBoqData.boqDescription?.length > 30 ? (
                                <span
                                  style={{
                                    color: "#2263b3",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                  }}
                                >
                                  {workBoqData.boqDescription.substring(0, 30)}
                                  ...
                                </span>
                              ) : (
                                workBoqData.boqDescription || "--"
                              )}
                            </Popover>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">
                            Completion Alert
                          </div>
                          <div className="data-value w-70">
                            <Badge color="outline-info">
                              {workBoqData.completionAlertsFrom
                                ? `${workBoqData.completionAlertsFrom}%`
                                : "Not Set"}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Created By</div>
                          <div className="data-value w-70">
                            {workBoqData.createdBy || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Created At</div>
                          <div className="data-value w-70">
                            {dayjs(workBoqData.createdAt).format("DD-MMM-YYYY")}
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Panel>

                  <Panel header="Work Order Lines" key="2">
                    <div className="table-responsive poTable">
                      <div
                        style={{
                          width: screenSize - 170 + "px",
                          margin: "auto",
                        }}
                      >
                        {!workBoqData.workOrderLines ||
                        workBoqData.workOrderLines.length === 0 ? (
                          <div className="text-center py-5">
                            <Empty description="No work order lines found" />
                          </div>
                        ) : (
                          <Table
                            className="customTable"
                            columns={columns}
                            dataSource={workBoqData.workOrderLines}
                            rowKey="id"
                            scroll={{ x: 1000 }}
                            expandable={{
                              expandedRowRender: (record: any) => (
                                <div className="expandableBody">
                                  <div style={{ flex: 1, textAlign: "left" }}>
                                    {record.taskDescription && (
                                      <div className="mb-3">
                                        <strong>Task Description:</strong>
                                        <p style={{ margin: "5px 0" }}>
                                          {record.taskDescription}
                                        </p>
                                      </div>
                                    )}

                                    {record.allocatedMaterials?.length > 0 && (
                                      <div className="mb-3">
                                        <strong>
                                          Allocated Materials (
                                          {record.allocatedMaterials.length}):
                                        </strong>
                                        <div className="mt-2">
                                          <Table
                                            className="customTable"
                                            columns={[
                                              {
                                                title: "Material",
                                                key: "material",
                                                width: "40%",
                                                render: (_, material: any) => (
                                                  <div>
                                                    <div className="fw-medium">
                                                      {typeof material ===
                                                      "string"
                                                        ? material
                                                        : material?.materialName ||
                                                          material?.name ||
                                                          "Unknown Material"}
                                                    </div>
                                                    {material?.category && (
                                                      <Tag color="blue">
                                                        {material.category}
                                                      </Tag>
                                                    )}
                                                  </div>
                                                ),
                                              },
                                              {
                                                title: "Quantity",
                                                key: "quantity",
                                                width: "20%",
                                                render: (_, material: any) => (
                                                  <span>
                                                    {material?.quantity || 0}{" "}
                                                    {material?.uomName ||
                                                      material?.uom ||
                                                      ""}
                                                  </span>
                                                ),
                                              },
                                              {
                                                title: "UOM",
                                                dataIndex: "uom",
                                                key: "uom",
                                                width: "15%",
                                                render: (
                                                  uom: string,
                                                  material: any
                                                ) => (
                                                  <Tag color="geekblue">
                                                    {material?.uomName ||
                                                      uom ||
                                                      "-"}
                                                  </Tag>
                                                ),
                                              },
                                            ]}
                                            dataSource={
                                              record.allocatedMaterials
                                            }
                                            rowKey={(material, index) =>
                                              material?.materialId || index
                                            }
                                            pagination={false}
                                            size="small"
                                            bordered
                                          />
                                        </div>
                                      </div>
                                    )}

                                    {record.budgetLineName && (
                                      <div className="mb-3">
                                        <strong>Budget Allocation:</strong>
                                        <div className="mt-2">
                                          <Tag color="blue">
                                            Budget Line: {record.budgetLineName}
                                          </Tag>
                                          <Tag color="cyan">
                                            Allocated Amount: ₹{formatDecimal(record.budgetAllocation || 0)}
                                          </Tag>
                                        </div>
                                      </div>
                                    )}

                                    {record.uploadedDocs?.length > 0 && (
                                      <div>
                                        <strong>Documents:</strong>
                                        <div style={{ marginTop: 8 }}>
                                          {record.uploadedDocs.map(
                                            (doc: any, index: number) => (
                                              <div
                                                key={index}
                                                style={{
                                                  marginLeft: "10px",
                                                  marginBottom: "4px",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "4px",
                                                }}
                                              >
                                                <PaperClipOutlined />
                                                <span>{doc.fileName}</span>
                                                {doc.filePath && (
                                                  <a
                                                    href={doc.filePath}
                                                    download={doc.fileName}
                                                    style={{
                                                      color: "#1890ff",
                                                      textDecoration: "none",
                                                      padding: "2px",
                                                      borderRadius: "2px",
                                                      fontSize: "14px",
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      cursor: "pointer",
                                                    }}
                                                    onMouseEnter={(e) => {
                                                      e.currentTarget.style.backgroundColor =
                                                        "#f0f0f0";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                      e.currentTarget.style.backgroundColor =
                                                        "transparent";
                                                    }}
                                                    onClick={(e) => {
                                                      // Force download instead of opening in new tab
                                                      e.preventDefault();
                                                      const link =
                                                        document.createElement(
                                                          "a"
                                                        );
                                                      link.href = doc.filePath;
                                                      link.download =
                                                        doc.fileName;
                                                      link.target = "_blank";
                                                      document.body.appendChild(
                                                        link
                                                      );
                                                      link.click();
                                                      document.body.removeChild(
                                                        link
                                                      );
                                                    }}
                                                  >
                                                    <DownloadOutlined />
                                                  </a>
                                                )}
                                              </div>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ),
                            }}
                            bordered
                            size="small"
                            pagination={false}
                          />
                        )}
                      </div>

                      {workBoqData.workOrderLines?.length > 0 && (
                        <Row style={{ margin: "20px" }}>
                          <Col className="justify-end">
                            <div className="w-40">
                              <div className="d-flex justify-content-between">
                                <div className="w-50 justify-end">
                                  <strong>Total Work Lines</strong>
                                </div>
                                <span className="r_align">
                                  {workBoqData.workOrderLines.length}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <div className="w-50 justify-end">
                                  <strong>Total Quantity</strong>
                                </div>
                                <span className="r_align">
                                  {formatDecimal(
                                    workBoqData.workOrderLines.reduce(
                                      (total: number, line: any) =>
                                        total + (Number(line.quantity) || 0),
                                      0
                                    )
                                  )}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <div className="w-50 justify-end">
                                  <strong>Work Categories</strong>
                                </div>
                                <span className="r_align">
                                  {
                                    new Set(
                                      workBoqData.workOrderLines.map(
                                        (line: any) => line.workCategory
                                      )
                                    ).size
                                  }
                                </span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <div className="w-50 justify-end">
                                  <strong>Total Budget Allocation</strong>
                                </div>
                                <span className="r_align">
                                  ₹{formatDecimal(
                                    workBoqData.workOrderLines.reduce(
                                      (total: number, line: any) =>
                                        total + (Number(line.budgetAllocation) || 0),
                                      0
                                    )
                                  )}
                                </span>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      )}
                    </div>
                  </Panel>
                </AntCollapse>
              </div>
            </div>
          </div>
        </Card>
      </Content>

      <Modal
        title="Instructions"
        visible={instructionsVisible}
        footer={null}
        onCancel={() => setInstructionsVisible(false)}
        width="60%"
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        {workBoqData.instructions ? (
          <pre>{workBoqData.instructions}</pre>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <Empty description="No instructions provided for this work BOQ" />
          </div>
        )}
      </Modal>
    </>
  );
};

export default WorkBoqView;
